import BoringAvatar from "boring-avatars";
import { cx } from "class-variance-authority";
import { useMemo } from "react";

import { avatar, image } from "./Avatar.module.scss";

type AvatarProps = {
  src?: string;
  alt: string;
  size?: number;
  name?: string;
  className?: string;
  colors?: string[];

  loading?: boolean;
};

// Default palette is the `*-9` step from each ramp — read at runtime so the
// generated avatar tracks token changes (and any future theming) without
// hard-coded hex drift.
const DEFAULT_RAMPS = [
  "--accent-9",
  "--info-9",
  "--neutral-9",
  "--neutral-7",
  "--warning-9",
] as const;

const readRampPalette = (vars: readonly string[]): string[] => {
  if (typeof window === "undefined") {
    return [];
  }
  const root = getComputedStyle(document.documentElement);
  return vars.map((name) => root.getPropertyValue(name).trim()).filter(Boolean);
};

const Avatar = ({
  src,
  alt,
  size = 16,
  name = "Generic Avatar",
  className,
  colors,
}: AvatarProps) => {
  const palette = useMemo(
    () => colors ?? readRampPalette(DEFAULT_RAMPS),
    [colors],
  );

  return (
    <div
      className={cx(avatar, className)}
      style={{ width: size, height: size }}
    >
      {src ? (
        <img className={image} src={src} alt={alt} />
      ) : (
        <BoringAvatar name={name} variant="beam" size={size} colors={palette} />
      )}
    </div>
  );
};

Avatar.displayName = "Avatar";

export { Avatar };
